import { getAuthFromLocalStorage, bbAxios, getCurrentProvider, STORAGE_KEYS } from '@bb/session-manager';
import axios from 'axios';
import { API_CONFIG_ENDPOINT } from '../endpoints.config';

/**
 * Utility function to set the document domain for OnlyOffice
 */
export function changeDocumentDomainForOnlyOffice() {
  // This sets the document domain to blackboiler.com or localhost to support only office
  const hostnameParts = window.location.hostname.split('.');
  if (hostnameParts.length > 1) {
    // looks like ['bb', 'blackboiler', 'com'] on bb.blackboiler.com, but ['localhost'] on localhost
    document.domain = [
      hostnameParts[hostnameParts.length - 2],
      hostnameParts[hostnameParts.length - 1],
    ].join('.');
  } else {
    document.domain = [
      // 0th element
      hostnameParts[hostnameParts.length - 1],
    ].join('.');
  }
}

/**
 * Fetch the configuration for the application.
 * When called before auth: uses plain axios (no auth token)
 * When called after auth: uses bbAxios (includes auth token from localStorage)
 * @returns {Promise<Object>} The configuration data.
 */
export async function fetchConfig() {
  // Try to use bbAxios if auth tokens are available, otherwise use plain axios
  // bbAxios will automatically add Authorization header from localStorage if tokens exist
  const authData = getAuthFromLocalStorage();
  // GCP: getAuthFromLocalStorage returns null (no OIDC appConfig), but bbAxios
  // handles GCP tokens natively via its request interceptor
  const hasGCPToken =
    getCurrentProvider() === 'gcp' && !!localStorage.getItem(STORAGE_KEYS.GCP_TOKEN);
  const axiosInstance = authData || hasGCPToken ? bbAxios : axios;

  const { data: config } = await axiosInstance.get(API_CONFIG_ENDPOINT);
  return config;
}

/**
 * Ensure user is provisioned in the system (auto-provisioning).
 * This must be called before any other user-specific API calls.
 * This is temporary until we can have an auto provision call outside of ensure authenticated
 * @returns {Promise<Object>} The provisioned user data.
 */
export async function ensureUserProvisioned(idToken) {
  // This endpoint will create/provision the user if they don't exist
  // It's the same call that the old AuthUpdateProvider made
  const { data: provisionedUser } = await bbAxios.post('/api/current-user', {
    // Note: You may need to pass idToken here depending on backend expectations
    idToken, // If needed
  });
  return provisionedUser;
}

/**
 * Fetch the current user's profile.
 * Backend returns computed permissions array.
 * @returns {Promise<Object>} The user profile data.
 */
export async function fetchCurrentBBUser(idToken) {
  // First ensure user is provisioned (this triggers auto-provisioning)
  await ensureUserProvisioned(idToken);

  // Then fetch the full user profile
  const { data: userObj } = await bbAxios.get('/api/v2/utility/auth/user', {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });
  // Backend returns permissions array, convert to Set for consistency
  userObj.permissions = new Set(userObj.permissions || []);
  return userObj;
}
